import { CheckIcon, Flex, Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { Fragment, forwardRef } from 'react';

import styles from './stepIndicator.module.css';

interface StepIndicatorProps {
  current: number;
  steps: string[];
}

function getStepAriaLabel(label: string, index: number, current: number) {
  const position = `Step ${index + 1}: ${label}`;
  if (index < current) return `${position} (completed)`;
  if (index === current) return `${position} (current)`;
  return position;
}

export const StepIndicator = forwardRef<HTMLDivElement, StepIndicatorProps>(
  function StepIndicator({ steps, current }, ref) {
    const label = steps[current];

    return (
      <Flex
        ref={ref}
        flexDirection="column"
        alignItems="center"
        gap={4}
        width="100%"
      >
        <Flex
          alignItems="center"
          width="100%"
          className={styles['progressBar']}
        >
          {steps.map((stepName, index) => {
            const isCompleted = index < current;
            const isCurrent = index === current;
            const ariaLabel = getStepAriaLabel(stepName, index, current);

            return (
              <Fragment key={stepName}>
                {index > 0 && (
                  <Flex
                    grow={1}
                    shrink={0}
                    minHeight={1}
                    minWidth={1}
                    className={clsx(styles['line'], {
                      [styles['lineActive']]: index <= current,
                    })}
                  />
                )}
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  shrink={0}
                  // Manually set height + width so the lines align with
                  // each step icon and not the icon + label
                  height={24}
                  width={24}
                  gap={4}
                >
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    shrink={0}
                    width={24}
                    height={24}
                    className={clsx(styles['step'], {
                      [styles['pendingStep']]: !isCompleted && !isCurrent,
                    })}
                    aria-label={ariaLabel}
                  >
                    {isCompleted && <CheckIcon size="sm" color="grays-white" />}
                    {isCurrent && <div className={styles['currentStep']} />}
                  </Flex>

                  <Typography
                    as="span"
                    variant="small-body-semibold"
                    color={index > current ? 'grays-mid' : 'grays-black'}
                    className={styles['stepLabel']}
                  >
                    {stepName}
                  </Typography>
                </Flex>
              </Fragment>
            );
          })}
        </Flex>

        <div className={styles['mobileLabel']}>
          <Typography
            as="span"
            variant="body-semibold"
            color="grays-black"
            textAlign="center"
          >
            {label}
          </Typography>
        </div>
      </Flex>
    );
  }
);
